import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '@shared/shared.module';
import { FinancialStatementType } from '@interfaces/accounting-data-standardization/responses/statement-detail.response';
import { StandardAccountDictionaryEntry } from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary.response';
import {
  entryKey,
  filterEntries,
  formatExclusion,
  groupEntriesByPath,
  statementTypeLabel,
} from '@src/app/features/dashboard/accounting-data-standardization/utils/standard-account-dictionary-governance.util';

type StatementFilter = FinancialStatementType | null;

const STATEMENT_FILTERS: { value: StatementFilter; label: string }[] = [
  { value: null, label: 'Todos' },
  { value: 'Dre', label: 'DRE' },
  { value: 'Balanco', label: 'Balanço' },
];

/**
 * Seção "Verbetes" da aba Dicionário (PBI 3423, DPA-DIC-DB): busca por texto, filtro DRE/Balanço e a lista agrupada
 * pelo caminho do plano padrão. Abrir um verbete mostra tudo (definição, entra, não entra com destino, natureza, prazo,
 * sinal, base normativa, erros comuns e decisões da casa) e, com MANAGE, o botão Editar. Só apresentação.
 */
@Component({
  selector: 'app-dictionary-entries-browser',
  standalone: true,
  imports: [FormsModule, SharedModule],
  templateUrl: './dictionary-entries-browser.component.html',
  styleUrl: './dictionary-entries-browser.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DictionaryEntriesBrowserComponent {
  readonly entries = input<StandardAccountDictionaryEntry[]>([]);
  readonly versionNumber = input<number | null>(null);
  readonly canManage = input(false);
  /** Outra ação do painel está em andamento: trava o Editar. */
  readonly locked = input(false);

  readonly editRequested = output<StandardAccountDictionaryEntry>();

  protected readonly statementFilters = STATEMENT_FILTERS;
  protected readonly query = signal('');
  protected readonly statementFilter = signal<StatementFilter>(null);
  protected readonly expandedKey = signal<string | null>(null);

  protected readonly filtered = computed(() =>
    filterEntries(this.entries(), this.statementFilter(), this.query())
  );
  protected readonly groups = computed(() => groupEntriesByPath(this.filtered()));

  protected readonly entryKey = entryKey;
  protected readonly statementTypeLabel = statementTypeLabel;
  protected readonly formatExclusion = formatExclusion;

  protected setStatementFilter(value: StatementFilter): void {
    this.statementFilter.set(value);
  }

  protected toggle(entry: StandardAccountDictionaryEntry): void {
    const key = entryKey(entry);
    this.expandedKey.update(current => (current === key ? null : key));
  }

  protected clearFilters(): void {
    this.query.set('');
    this.statementFilter.set(null);
  }

  protected hasTraits(entry: StandardAccountDictionaryEntry): boolean {
    return !!(entry.normalBalance || entry.termCriterion || entry.expectedSign);
  }
}
